import classNames from "classnames";
import {
	AlertBar,
	Button,
	Dialog,
	DialogBackdropState,
	Form,
	InputRow,
	Select,
	SelectItem,
	Step,
	Stepper,
} from "client/Components";
import { IObjectInputAttachable } from "client/Forms/attachable";
import {
	generateTrackingId,
	isNotNil,
	setTrackingId
} from "client/Misc/utils";
import {
	PropsWithChildren,
	ReactElement,
	useMemo
} from "react";
import { localize } from "client_legacy/ndaw/culture";
import styles from "./HubItemComposerDialog.module.less";
import { HubHierarchyListNodeModel } from "client/Api/Hub/common.composer";
import { convertHubHierarchyListNodeToSelectItem } from "../common";

export type HubItemComposerDialogProps<TStep> = {
	isBusy: boolean;
	title: string;
	onClose(): void;
	onBack?(): void;
	goToNextStep(): void;
	activeStep: TStep | nil;
	onActiveStepChanged(value: TStep): void;
	steps: readonly Step<TStep>[];
	onShowInfo?(): void;
	trackingId?: string;
	footer: ReactElement;
	preview: ReactElement | null;
	backdrop?: DialogBackdropState;
};

export const HubItemComposerDialog = <TStep, >(
	props: PropsWithChildren<HubItemComposerDialogProps<TStep>>
): ReactElement => {
	const {
		isBusy,
		title,
		onClose,
		onBack,
		onShowInfo,
		goToNextStep,
		activeStep,
		onActiveStepChanged,
		steps,
		trackingId,
		children,
		footer,
		preview,
		backdrop
	} = props;

	return (
		<Dialog
			variant="left-slider"
			onClose={onClose}
			hasBusyIndicator={isBusy}
			trackingId={trackingId}
			backdrop={backdrop}
		>
			<Dialog.Header
				title={title}
				onClose={onClose}
				onBack={onBack}
				onShowInfo={onShowInfo}
				hasBorder
			/>
			<Dialog.Body
				goesEdgeToEdge
			>
				{isNotNil(preview) && (
					<div className={styles.previewContainer}>
						{preview}
					</div>
				)}
				<div className={classNames(
					styles.formContainer,
					{ [styles.formContainer_hidden]: isNotNil(preview) }
				)}>
					<Form
						onSubmit={goToNextStep}
						height="fill"
					>
						<div className={styles.stepper}>
							<Stepper
								trackingId={generateTrackingId(trackingId, "__stepper")}
								value={activeStep}
								onChangeStep={onActiveStepChanged}
								steps={steps}
							/>
						</div>
						{children}
					</Form>
				</div>
			</Dialog.Body>
			<Dialog.Footer hasBorder>
				{footer}
			</Dialog.Footer>
		</Dialog>
	);
};
HubItemComposerDialog.displayName="HubItemComposerDialog";

type ComposerStepProps = {
	isActive: boolean;
	trackingId?: string;
};

const ComposerStep = (props: PropsWithChildren<ComposerStepProps>): ReactElement => {
	const {
		isActive,
		trackingId,
		children
	} = props;

	return (
		<div
			{...setTrackingId(trackingId)}
			className={classNames(
				styles.step,
				{
					[styles.step_hide]: !isActive
				}
			)}
		>
			{children}
		</div>
	);
};
ComposerStep.displayName = "ComposerStep";

type ToolbarProps = {
	isToolbarVisible: boolean;
};

const ToolbarContainer = (props: PropsWithChildren<ToolbarProps>): ReactElement => {
	const {
		children
	} = props;

	return (
		<div
			className={styles.toolbarContainer}
		>
			<div className={styles.toolbarScrollable}>
				{children}
			</div>
		</div>
	);
};
ToolbarContainer.displayName = "ToolbarContainer";

type ContentProps = {
	stretchHeight?: boolean;
	hasNoPadding?: boolean;
};

const Content = (props: PropsWithChildren<ContentProps>): ReactElement => {
	const {
		stretchHeight = false,
		hasNoPadding = false,
		children
	} = props;

	return (
		<div
			className={classNames(
				styles.content,
				{
					[styles.content_stretchHeight]: stretchHeight,
					[styles.content_noPadding]: hasNoPadding
				}
			)}
		>
			{children}
		</div>
	);
};
Content.displayName = "Content";

const FormContainer = (props: PropsWithChildren<{}>): ReactElement => {
	const {
		children
	} = props;

	return (
		<div className={styles.formItemsContainer}>
			{children}
		</div>
	);
};
FormContainer.displayName = "FormContainer";

const BuilderContainer = (props: PropsWithChildren<{}>): ReactElement => {
	const {
		children
	} = props;

	return (
		<div className={styles.builderContainer}>
			{children}
		</div>
	);
};
BuilderContainer.displayName = "BuilderContainer";

const BuilderLayoutContainer = (props: PropsWithChildren<{}>): ReactElement => {
	const {
		children
	} = props;

	return (
		<div className={styles.builderLayoutContainer}>
			{children}
		</div>
	);
};
BuilderLayoutContainer.displayName = "BuilderLayoutContainer";

type BuilderTitleProps = {
	hasError: boolean;
};

const BuilderTitle = (props: PropsWithChildren<BuilderTitleProps>): ReactElement => {
	const {
		children,
		hasError
	} = props;

	return (
		<div className={
			classNames(
				styles.builderTitle,
				{
					[styles.builderTitle_hasError]: hasError
				}
			)
		}>
			{children}
		</div>
	);
};
BuilderTitle.displayName = "BuilderTitle";

type TitleProps = {
	text: string;
};

const Title = (props: TitleProps): ReactElement => {
	const {
		text
	} = props;

	return (
		<h3 className={styles.title}>{text}</h3>
	);
};
Title.displayName = "Title";

type SectionTitleProps = {
	text: string;
};

const SectionTitle = (props: SectionTitleProps): ReactElement => {
	const {
		text
	} = props;

	return (
		<h4 className={styles.sectionTitle}>{text}</h4>
	);
};
SectionTitle.displayName = "SectionTitle";

type AlertBarProps = {
	text: string;
	trackingId: string;
	onDismiss(): void;
};

const ComposerAlertBar = (props: AlertBarProps): ReactElement => {
	const {
		text,
		trackingId,
		onDismiss
	} = props;

	return (
		<div {...setTrackingId("alert-bar")} className={styles.alertBarContainer}>
			<AlertBar
				kind="info"
				onDismiss={onDismiss}
				trackingId={trackingId}
				isInline
			>
				{text}
			</AlertBar>
		</div>
	);
};
ComposerAlertBar.displayName = "ComposerAlertBar";

type CategorySelectProps = {
	attachable: IObjectInputAttachable<number | null>;
	categoryHierarchy: readonly HubHierarchyListNodeModel[];
	hasPermissionToCreateNewCategory: boolean;
	isArchived: boolean;
	onCreateNewCategory(): void;
	menuTitle: string;
};

const CategorySelect = (props: PropsWithChildren<CategorySelectProps>): ReactElement => {
	const {
		hasPermissionToCreateNewCategory,
		isArchived,
		onCreateNewCategory,
		categoryHierarchy,
		attachable,
		menuTitle
	} = props;

	const categoryOptions = useMemo(
		(): readonly SelectItem<number>[] => {
			return convertHubHierarchyListNodeToSelectItem(categoryHierarchy);
		},
		[categoryHierarchy]
	);

	return (
		<InputRow>
			<InputRow.Stretch>
				<Select
					{...attachable.attachToSelect()}
					options={categoryOptions}
					isDisabled={isArchived}
					trackingId="select-category"
					menuTitle={menuTitle}
					doesSupportFiltering
					placeholderPropertyNames={[
						localize("_CategoryName")
					]}
				/>
			</InputRow.Stretch>

			{(hasPermissionToCreateNewCategory && !isArchived) && (
				<Button
					kind="secondary"
					text={localize("_New")}
					onClick={onCreateNewCategory}
					trackingId="create-new-category-button"
				/>
			)}
		</InputRow>
	);
};
CategorySelect.displayName = "CategorySelect";

HubItemComposerDialog.Step = ComposerStep;
HubItemComposerDialog.ToolbarContainer = ToolbarContainer;
HubItemComposerDialog.Content = Content;
HubItemComposerDialog.FormContainer = FormContainer;
HubItemComposerDialog.BuilderContainer = BuilderContainer;
HubItemComposerDialog.BuilderTitle = BuilderTitle;
HubItemComposerDialog.BuilderLayoutContainer = BuilderLayoutContainer;
HubItemComposerDialog.Title = Title;
HubItemComposerDialog.SectionTitle = SectionTitle;
HubItemComposerDialog.CategorySelect = CategorySelect;
HubItemComposerDialog.AlertBar = ComposerAlertBar;